import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, type KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRole } from "../../components/AppShell";
import { AttachIcon, BackIcon, InfoIcon, SendIcon, StopwatchIcon } from "../../components/icons";
import { ChatSkeleton, StatusPill, useToast } from "../../components/ui";
import { WhatsAppText } from "../../components/WhatsAppText";
import { ApiError } from "../../lib/api";
import { CONTACT_TYPE_LABEL, clock, dayLabel, failureText, formatPhone, windowLeft } from "../../lib/format";
import { isWaiting, useNow, waitLabel, waitLevel, waitMinutes } from "../../lib/inbox";
import {
  useAssign,
  useContact,
  useConversation,
  useMarkNotificationsRead,
  useMe,
  useMessages,
  useNotifications,
  useQuickReplies,
  useSendMessage,
  useSetStatus,
  useTeam,
} from "../../lib/queries";
import type { Message } from "../../lib/types";
import { ContactPanel } from "./ContactPanel";
import { TemplatePicker } from "./TemplatePicker";

const DELIVERY_LABEL: Record<Message["deliveryStatus"], string> = {
  received: "",
  pending: "Enviando…",
  sent: "Enviado",
  delivered: "Entregado",
  read: "Leído",
  failed: "No se envió",
};

export function ConversationView({ conversationId }: { conversationId: string }) {
  const conv = useConversation(conversationId);
  const messages = useMessages(conversationId);
  const me = useMe();
  const team = useTeam();
  const role = useRole();
  const assign = useAssign(conversationId);
  const setStatus = useSetStatus(conversationId);
  const toast = useToast();
  const sheet = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const now = useNow();
  const contact = useContact(conv.data?.contact.id);
  const { mutate: readNotifications } = useMarkNotificationsRead();
  const unreadHere = useNotifications().data?.items.some((n) => n.conversationId === conversationId && !n.readAt);
  // Opening the chat reads what was notified about it.
  useEffect(() => {
    if (unreadHere) readNotifications({ conversationId });
  }, [conversationId, unreadHere, readNotifications]);

  const count = messages.data?.length ?? 0;
  const box = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Follows new messages while the reader is at the bottom; scrolling up to read stops it.
  const stick = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when the message count changes
  useLayoutEffect(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
    stick.current = true;
  }, [count]);
  // The contact facts arrive after the messages and push the list up: stay at the end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run when the header changes height
  useLayoutEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [contact.data?.id, conv.data?.status]);
  // The header (contact facts, waiting alert) loads after the messages and shrinks the box.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-attach once the list is rendered
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    // Content also grows after the first paint (team names, the IA block opening).
    if (listRef.current) observer.observe(listRef.current);
    return () => observer.disconnect();
  }, [conv.isPending]);

  const names = useMemo(() => new Map(team.data?.items.map((m) => [m.id, m.name])), [team.data]);

  if (conv.isPending) {
    return (
      <section className="chat" aria-label="Conversación">
        <ChatSkeleton />
      </section>
    );
  }
  if (conv.isError) return <div className="chat-empty">{conv.error.message}</div>;

  const c = conv.data;
  const myId = me.data?.user.id;
  const canAct = role !== "agent" || !c.assigneeId || c.assigneeId === myId;
  const waiting = isWaiting(c);
  const minutes = waiting ? waitMinutes(c.lastInboundAt, now) : 0;
  const level = waitLevel(minutes);

  // Lumi's replies stay in the chat like any other (purple bubbles), never folded away.
  const list = messages.data ?? [];

  const run = (p: Promise<unknown>, done: string) =>
    p.then(() => toast(done)).catch((e: unknown) => toast(e instanceof ApiError ? e.message : "No se pudo completar."));

  const actions = (
    <>
      {!waiting && c.assigneeId !== myId && canAct && myId && (
        <button className="btn btn-sm" type="button" onClick={() => run(assign.mutateAsync(myId), "Ahora es tuya")}>
          Tomar
        </button>
      )}
      {role !== "agent" && (
        <select
          className="select btn-sm"
          aria-label="Asignar a"
          value={c.assigneeId ?? ""}
          onChange={(e) => run(assign.mutateAsync(e.target.value || null), "Asignación actualizada")}
        >
          <option value="">Sin asignar</option>
          {team.data?.items.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      )}
      {c.status !== "bot" && canAct && (
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => run(setStatus.mutateAsync("bot"), "Devuelta a la IA")}
        >
          Devolver a la IA
        </button>
      )}
      {c.status !== "closed" ? (
        canAct && (
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => run(setStatus.mutateAsync("closed"), "Conversación cerrada")}
          >
            Cerrar
          </button>
        )
      ) : (
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => run(setStatus.mutateAsync("human"), "Conversación reabierta")}
        >
          Reabrir
        </button>
      )}
    </>
  );

  const renderMessage = (m: Message) => {
    const i = list.indexOf(m);
    const prev = list[i - 1];
    const showDay = !prev || dayLabel(prev.sentAt) !== dayLabel(m.sentAt);
    const kind = m.direction === "inbound" ? "in" : `out ${m.author === "agent_human" ? "human" : "ai"}`;
    return (
      <div key={m.id} style={{ display: "contents" }}>
        {showDay && <span className="day">{dayLabel(m.sentAt)}</span>}
        <div className={`bubble ${kind}${m.deliveryStatus === "failed" ? " failed" : ""}`}>
          {m.document && (
            <span className="bubble-doc">
              <AttachIcon />
              {m.document.quoteId ? (
                <Link to="/cotizaciones/$quoteId" params={{ quoteId: m.document.quoteId }}>
                  {m.document.filename}
                </Link>
              ) : (
                m.document.filename
              )}
            </span>
          )}
          {m.body ? <WhatsAppText text={m.body} /> : <em className="muted">[{m.type}]</em>}
          <div className="meta">
            <span>
              {m.direction === "inbound"
                ? "Cliente"
                : m.author === "agent_human"
                  ? (names.get(m.authorUserId ?? "") ?? "Asesor")
                  : m.author === "agent_ai"
                    ? "IA"
                    : "Respuesta automática"}
            </span>
            <span>{clock(m.sentAt)}</span>
            {m.direction === "outbound" && (
              <span className={m.deliveryStatus === "failed" ? "failed-note" : ""}>
                {DELIVERY_LABEL[m.deliveryStatus]}
              </span>
            )}
          </div>
          {m.deliveryStatus === "failed" && m.failureCode !== null && (
            <p className="bubble-failure">{failureText(m.failureCode)}</p>
          )}
        </div>
      </div>
    );
  };

  const card = contact.data;
  const facts = [
    card?.type ? ["Tipo", CONTACT_TYPE_LABEL[card.type]] : null,
    card?.province ? ["Provincia", card.province] : null,
    card?.tags.length
      ? ["Etiquetas", card.tags.slice(0, 3).join(", ") + (card.tags.length > 3 ? ` +${card.tags.length - 3}` : "")]
      : null,
  ].filter((f): f is [string, string] => f !== null);

  async function takeAndReply() {
    if (!myId) return;
    await run(assign.mutateAsync(myId), "Ahora es tuya");
    document.getElementById("composer-input")?.focus();
  }

  return (
    <section className="chat" aria-label="Conversación">
      <header className="chat-head">
        <div className="chat-head-line">
          <button
            className="btn btn-ghost btn-icon only-phone"
            type="button"
            aria-label="Volver a la bandeja"
            onClick={() => navigate({ to: "/bandeja", search: (s) => ({ ...s, c: undefined }) })}
          >
            <BackIcon />
          </button>
          <div className="title">
            <strong title={c.contact.name ?? undefined}>{c.contact.name ?? formatPhone(c.contact.waId)}</strong>
            <span className="muted">
              {c.contact.name ? `${formatPhone(c.contact.waId)} · ` : ""}
              {c.assigneeId ? `Asignada a ${names.get(c.assigneeId) ?? "…"}` : "Sin asignar"}
            </span>
          </div>
          <StatusPill status={c.status} />
          <div className="actions hide-phone">{actions}</div>
          <button
            className="btn btn-ghost btn-icon"
            type="button"
            aria-label="Ver contacto y acciones"
            title="Ficha del contacto"
            onClick={() => sheet.current?.showModal()}
          >
            <InfoIcon />
          </button>
        </div>
        {(facts.length > 0 || card) && (
          <div className="chat-facts hide-phone">
            {facts.map(([k, v]) => (
              <span key={k}>
                {k} <b>{v}</b>
              </span>
            ))}
            {card && (
              <span>
                <b>{card.conversations.length}</b> {card.conversations.length === 1 ? "conversación" : "conversaciones"}
              </span>
            )}
          </div>
        )}
        {waiting && (
          <div className={`wait-alert ${level === "late" ? "late" : ""}`}>
            <span>
              <StopwatchIcon />
              Esperando respuesta hace {waitLabel(minutes)}
            </span>
            {c.assigneeId !== myId && canAct && myId && (
              <button className="btn btn-primary btn-sm" type="button" onClick={takeAndReply}>
                Tomar y responder
              </button>
            )}
          </div>
        )}
      </header>

      <div
        className="messages"
        ref={box}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
      >
        <div className="messages-list" ref={listRef}>
          {messages.isPending && <ChatSkeleton />}
          {list.map(renderMessage)}
        </div>
      </div>

      <dialog className="sheet" ref={sheet} aria-label="Contacto y acciones">
        <div className="sheet-head">
          <strong>Contacto y acciones</strong>
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => sheet.current?.close()}>
            Listo
          </button>
        </div>
        <div className="sheet-actions">{actions}</div>
        <ContactPanel conversationId={conversationId} inSheet />
      </dialog>

      <Composer
        conversationId={conversationId}
        windowOpen={c.windowOpen}
        lastInboundAt={c.lastInboundAt}
        status={c.status}
        canAct={canAct}
      />
    </section>
  );
}

function Composer({
  conversationId,
  windowOpen,
  lastInboundAt,
  status,
  canAct,
}: {
  conversationId: string;
  windowOpen: boolean;
  lastInboundAt: string | null;
  status: string;
  canAct: boolean;
}) {
  const [text, setText] = useState("");
  const [menuIndex, setMenuIndex] = useState(0);
  const [showTemplates, setShowTemplates] = useState(false);
  const [picking, setPicking] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);

  // Grows with the text up to about six lines, then scrolls inside (like WhatsApp).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure whenever the text changes
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 152)}px`;
    el.style.overflowY = el.scrollHeight > 152 ? "auto" : "hidden";
  }, [text]);
  const send = useSendMessage(conversationId);
  const quick = useQuickReplies();
  const toast = useToast();

  // "/garantia" opens the quick reply menu, filtered by what follows the slash.
  const slash = text.startsWith("/") && !text.includes("\n") ? text.slice(1).toLowerCase() : null;
  const matches = useMemo(
    () =>
      slash === null
        ? []
        : (quick.data ?? [])
            .filter((q) => q.shortcut.includes(slash) || q.title.toLowerCase().includes(slash))
            .slice(0, 8),
    [slash, quick.data],
  );

  if (!canAct) {
    return (
      <div className="composer">
        <div className="banner banner-info">Esta conversación está asignada a otra persona del equipo.</div>
      </div>
    );
  }
  if (!windowOpen) {
    return (
      <div className="composer">
        <div className="banner banner-warn">
          Pasaron más de 24 horas desde el último mensaje del cliente. WhatsApp solo permite escribirle con una
          plantilla aprobada.
        </div>
        {picking ? (
          <TemplatePicker conversationId={conversationId} onDone={() => setPicking(false)} />
        ) : (
          <button className="btn btn-primary" type="button" onClick={() => setPicking(true)}>
            Elegir plantilla
          </button>
        )}
      </div>
    );
  }

  if (showTemplates) {
    return (
      <div className="composer">
        <TemplatePicker conversationId={conversationId} onDone={() => setShowTemplates(false)} />
      </div>
    );
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    const body = text.trim();
    if (!body || send.isPending) return;
    try {
      const sent = await send.mutateAsync(body);
      setText("");
      if (sent.deliveryStatus === "failed") toast("WhatsApp no aceptó el mensaje. Revisa el número o el token.");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo enviar.");
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (matches.length) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setMenuIndex((i) => (i + (e.key === "ArrowDown" ? 1 : matches.length - 1)) % matches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        setText(matches[menuIndex]?.body ?? text);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  const left = windowLeft(lastInboundAt);

  return (
    <div className="composer">
      {matches.length > 0 && (
        <div className="quick-menu" role="listbox" aria-label="Respuestas rápidas">
          {matches.map((q, i) => (
            <button
              key={q.id}
              type="button"
              role="option"
              aria-selected={i === menuIndex}
              onClick={() => setText(q.body)}
            >
              <strong>
                /{q.shortcut} · {q.title}
              </strong>
              <small>{q.body}</small>
            </button>
          ))}
        </div>
      )}
      {status === "bot" && <div className="banner banner-info">Al responder, la conversación pasa de la IA a ti.</div>}
      {(quick.data?.length ?? 0) > 0 && (
        <fieldset className="quick-chips">
          <legend className="sr-only">Respuestas rápidas</legend>
          {quick.data?.slice(0, 4).map((q) => (
            <button
              key={q.id}
              type="button"
              title={q.body}
              onClick={() => {
                setText(q.body);
                input.current?.focus();
              }}
            >
              {q.title}
            </button>
          ))}
        </fieldset>
      )}
      <form className="composer-box" onSubmit={submit}>
        <textarea
          ref={input}
          id="composer-input"
          className="composer-input"
          aria-label="Mensaje"
          placeholder="Mensaje (/ para atajos)"
          value={text}
          rows={1}
          enterKeyHint="send"
          onChange={(e) => {
            setText(e.target.value);
            setMenuIndex(0);
          }}
          onKeyDown={onKeyDown}
        />
        <button
          className="btn btn-primary send-btn"
          type="submit"
          disabled={!text.trim() || send.isPending}
          aria-label="Enviar"
        >
          <SendIcon />
        </button>
      </form>
      <div className="composer-foot">
        {left && <small className="faint">Ventana de 24 h abierta, {left}.</small>}
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setShowTemplates(true)}>
          Usar plantilla
        </button>
      </div>
    </div>
  );
}
